import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Form, Table as BsTable } from "react-bootstrap";
import LoadingSpinner from "../../../Components/Loading/LoadingSpinner";
import Pagination from "../../../Components/Pagination/Pagination";
import { fetchStoreRequests } from "../../../store/actions/MarketSlice";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Declined" },
  { value: "all", label: "All" },
];

const STATUS_COLORS = {
  pending: "#f5a623",
  approved: "#01b574",
  declined: "#ff1733",
};

const WALLET_STATUS_OPTIONS = [
  { value: "", label: "All wallets" },
  { value: "ok", label: "OK" },
  { value: "grace", label: "Grace period" },
  { value: "deactivated", label: "Deactivated" },
];

const WALLET_COLORS = {
  ok: "#01b574",
  grace: "#f5a623",
  deactivated: "#ff1733",
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || "#a0aec0";
  return (
    <span
      className="text-uppercase fw-bold"
      style={{
        color,
        border: `1px solid ${color}`,
        borderRadius: "12px",
        padding: "2px 10px",
        fontSize: "0.75rem",
      }}
    >
      {status}
    </span>
  );
}

function WalletBadge({ status }) {
  const color = WALLET_COLORS[status] || "#a0aec0";
  return (
    <span
      className="text-uppercase fw-bold"
      style={{
        color,
        border: `1px solid ${color}`,
        borderRadius: "12px",
        padding: "2px 10px",
        fontSize: "0.75rem",
      }}
    >
      {status || "—"}
    </span>
  );
}

// Shared by both the "Store Requests" tab (approval queue, defaultStatus
// "pending") and the "Stores" tab (defaultStatus "approved",
// showWalletFilter). The wallet filter/column only make sense once a store
// is live, so they're opt-in rather than always shown.
export default function StoreRequestsList({
  defaultStatus = "pending",
  showWalletFilter = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { storeRequests, storeRequestsMeta, loading } = useSelector(
    (state) => state.market
  );
  const [status, setStatus] = useState(defaultStatus);
  const [walletStatus, setWalletStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setStatus(defaultStatus);
    setWalletStatus("");
    setCurrentPage(1);
  }, [defaultStatus]);

  useEffect(() => {
    dispatch(
      fetchStoreRequests({ status, walletStatus, page: currentPage, limit: 10 })
    );
  }, [dispatch, status, walletStatus, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <div
        className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4"
        data-aos="fade-up"
        data-aos-duration="600"
      >
        <div className="d-flex align-items-center gap-3">
          {STATUS_OPTIONS.map((opt) => (
            <span
              key={opt.value}
              onClick={() => {
                setStatus(opt.value);
                setCurrentPage(1);
              }}
              className={`pointer fw-bold text-grey ${
                status === opt.value && "active-table"
              }`}
            >
              {opt.label}
            </span>
          ))}
        </div>

        {showWalletFilter && (
          <Form.Select
            value={walletStatus}
            onChange={(e) => {
              setWalletStatus(e.target.value);
              setCurrentPage(1);
            }}
            style={{ maxWidth: 200, backgroundColor: "#2d2d2d", color: "#ecf0f1", border: "1px solid #495057" }}
          >
            {WALLET_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        )}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="text-white" style={{ overflowX: "auto" }}>
            <BsTable variant="dark" borderless responsive className="align-middle">
              <thead>
                <tr className="text-grey">
                  <th>Store Name</th>
                  <th>Owner</th>
                  <th>Currency</th>
                  <th>Status</th>
                  {showWalletFilter && <th>Wallet</th>}
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {storeRequests?.length > 0 ? (
                  storeRequests.map((store) => (
                    <tr
                      key={store.id}
                      style={{ borderBottom: "#56577A 1px solid" }}
                    >
                      <td>{store?.name || "—"}</td>
                      <td>{store?.owner?.name || "—"}</td>
                      <td>{store?.currency || "—"}</td>
                      <td>
                        <StatusBadge status={store?.approval_status} />
                      </td>
                      {showWalletFilter && (
                        <td>
                          <WalletBadge status={store?.wallet_status} />
                        </td>
                      )}
                      <td>
                        {store?.created_at
                          ? new Date(store.created_at * 1000).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <Button
                          onClick={() =>
                            navigate(`/dashboard/market/store-requests/${store.id}`)
                          }
                          className="border-0 text-uppercase text-white rounded bg-custom-gray"
                        >
                          Review
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={showWalletFilter ? 7 : 6}>
                      <h5 className="my-5 text-center text-grey">NO DATA YET</h5>
                    </td>
                  </tr>
                )}
              </tbody>
            </BsTable>
          </div>
          <Pagination
            meta={storeRequestsMeta}
            onPageChange={handlePageChange}
            loading={loading}
            className="mt-4"
          />
        </>
      )}
    </div>
  );
}
